import React, { useMemo } from "react";
import { Dialog } from "@actinc/dls";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";

import { getWorkspaceURIFromTask } from "../../../../services/utils";

const MissingRequirementsDialog = ({
  onClose,
  open,
  prerequisites,
  tasks,
  types,
}) => {
  const objects = useMemo(
    () =>
      types.map((type) => (tasks.length > 1 ? `${type}s` : type)).join(" and "),
    [types],
  );

  const handleClose = (task) => () => {
    if (!task) return onClose(false);
    const uri = getWorkspaceURIFromTask(task);
    onClose(uri);
  };

  return (
    <Dialog maxWidth="md" onClose={handleClose(false)} open={open}>
      <DialogTitle>
        Missing requirement{prerequisites.length > 1 && "s"}
      </DialogTitle>
      <DialogContent>
        {prerequisites.map((prerequisite) => {
          if (
            prerequisite === "noOutstandingTasks" &&
            tasks.some(
              (task) =>
                task.outstandingTasks.filter(
                  (s) => task.taskDefinition.key !== s,
                ).length,
            )
          ) {
            if (tasks.length === 1) {
              return (
                <Box key={prerequisite} sx={{ marginTop: 2 }}>
                  <Typography>
                    The selected task cannot be completed due to other
                    outstanding tasks.
                  </Typography>
                </Box>
              );
            } else {
              return (
                <React.Fragment key={prerequisite}>
                  <Typography>
                    The tasks associated with the following {objects} cannot be
                    completed due to other outstanding tasks.
                  </Typography>
                  <List dense={true}>
                    {tasks.map((task) => {
                      let primary, secondary;
                      if (task.item) {
                        primary = task.item.name;
                        secondary = "Item";
                      }
                      if (task.passage) {
                        primary = task.passage.name;
                        secondary = "Passage";
                      }
                      if (
                        task.outstandingTasks.filter(
                          (s) => task.taskDefinition.key !== s,
                        ).length
                      ) {
                        return (
                          <ListItem key={task.id}>
                            <ListItemText
                              primary={primary}
                              secondary={secondary}
                            />
                          </ListItem>
                        );
                      }
                    })}
                  </List>
                </React.Fragment>
              );
            }
          }
          if (
            prerequisite === "requireQRR" &&
            tasks.some((task) => !task.qrr)
          ) {
            if (tasks.length === 1) {
              return (
                <Box key={prerequisite} sx={{ marginTop: 2 }}>
                  <Typography>
                    The selected task is missing a Quality Rubric Rating.
                  </Typography>
                </Box>
              );
            } else {
              return (
                <React.Fragment key={prerequisite}>
                  <Typography>
                    The tasks associated with the following {objects} are
                    missing a Quality Rubric Rating. Please ensure that you
                    update their Quality Rubric Rating prior to applying bulk
                    actions.
                  </Typography>
                  <List dense={true}>
                    {tasks.map((task) => {
                      let primary, secondary;
                      if (task.item) {
                        primary = task.item.name;
                        secondary = "Item";
                      }
                      if (task.passage) {
                        primary = task.passage.name;
                        secondary = "Passage";
                      }
                      if (!task.qrr) {
                        return (
                          <ListItem key={task.id}>
                            <ListItemText
                              primary={primary}
                              secondary={secondary}
                            />
                          </ListItem>
                        );
                      }
                    })}
                  </List>
                </React.Fragment>
              );
            }
          }
          if (
            prerequisite === "resolveChangeTags" &&
            tasks.some(
              (task) =>
                task.passage?.hasUnresolvedChangeTags ||
                task.item?.hasUnresolvedChangeTags,
            )
          ) {
            if (tasks.length === 1) {
              return (
                <Box key={prerequisite} sx={{ marginTop: 2 }}>
                  <Typography>
                    The selected task cannot be completed due to unresolved
                    changes in content.
                  </Typography>
                </Box>
              );
            } else {
              return (
                <React.Fragment key={prerequisite}>
                  <Typography>
                    The tasks associated with the following {objects} cannot be
                    completed due to unresolved changes in content. Please
                    ensure that you accept or reject all changes prior to
                    applying bulk actions.
                  </Typography>
                  <List dense={true}>
                    {tasks.map((task) => {
                      let primary, secondary;
                      if (task.item) {
                        primary = task.item.name;
                        secondary = "Item";
                      }
                      if (task.passage) {
                        primary = task.passage.name;
                        secondary = "Passage";
                      }
                      if (
                        task.item?.hasUnresolvedChangeTags ||
                        task.passage?.hasUnresolvedChangeTags
                      ) {
                        return (
                          <ListItem key={task.id}>
                            <ListItemText
                              primary={primary}
                              secondary={secondary}
                            />
                          </ListItem>
                        );
                      }
                    })}
                  </List>
                </React.Fragment>
              );
            }
          }
        })}
      </DialogContent>
      <DialogActions>
        {tasks.length === 1 && !prerequisites.includes("noOutstandingTasks") ? (
          <>
            <Button onClick={handleClose(false)} variant="text">
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleClose(tasks[0])}
              type="button"
              variant="contained"
            >
              Open Workspace
            </Button>
          </>
        ) : (
          <Button
            color="primary"
            onClick={handleClose(false)}
            type="button"
            variant="contained"
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export { MissingRequirementsDialog };
